import { useState } from 'react';
import dayjs from 'dayjs';
import { Card, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router';

import { Strings } from '@app/constants';
import { StyledTitle } from '@app/modules/dashboard/style';
import { DataNotFound, Iconify, Scrollbar } from '@app/components';
import { InfoStatusCard } from '@app/components/card/info-status-card';
import { managersNames } from '@app/mock/taskBoard';
import { useSearch } from '@app/hooks';
import { CustomAvatarGroup, CustomAvatar } from '@app/components/custom-avatar';
import { MemberListPopover } from '@app/components/card/member-list-Popover';
import { IAssets } from '@app/modules/assets';
import { IManagerDashboard, IProject } from '../type';
import { PATH_DASHBOARD } from '@app/routes/paths';

interface props {
  data?: IManagerDashboard;
  taskAssignment: any;
}
const MangerAssetsAndTasksPage = ({ data, taskAssignment }: props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const addSelectedMember = (name: string) => {
    const updatedMembers = selectedMembers.includes(name)
      ? selectedMembers.filter((member: string) => member !== name)
      : [...selectedMembers, name];
    setSelectedMembers(updatedMembers);
  };

  const searchProps = useSearch({
    data: managersNames,
    fields: ['label'],
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget as any);
  };

  const theme = useTheme();
  const navigate = useNavigate();

  const filteredTasks =
    selectedMembers.length > 0
      ? taskAssignment?.filter((task: any) => selectedMembers.includes(task.assignedTo.firstName))
      : taskAssignment;

  const getUniqueManagers = (managers: IProject[]) => {
    const uniqueNames = new Set();
    return managers.filter((manager: any) => {
      const name = manager.assignedTo.firstName;
      if (uniqueNames.has(name)) {
        return false;
      }
      uniqueNames.add(name);
      return true;
    });
  };

  const clearFilters = () => {
    setSelectedMembers([]);
  };

  return (
    <Stack flexDirection={{ sm: 'column', md: 'row' }} mb={3}>
      <Card
        sx={{
          width: { sm: '97%', md: '50%' },
          height: { md: '300px' },
          maxHeight: { xs: '300px' },
          margin: '0 2% 0 2%',
          marginTop: { xs: '2%', sm: '2%', md: '0%' },
          borderRadius: '5px',
        }}
      >
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 2,
          }}
        >
          <StyledTitle>{Strings.employeeDashborad.taskAssignment}</StyledTitle>
          {filteredTasks?.length > 0 && (
            <Stack
              sx={{
                flexDirection: 'row',
                border: `1px solid ${theme.palette.grey[500]}`,
                alignItems: 'center',
                borderRadius: '13px',
                cursor: 'pointer',
                width: '30%',
                height: '30px',
              }}
              gap={{ md: 1, xs: 0.6 }}
              p={{ md: 1, xs: 0.5 }}
              mt={1}
            >
              <IconButton
                aria-label="close"
                sx={{
                  color: theme.palette.primary.dark,
                  p: 0,
                }}
                onClick={handleClick}
              >
                <Iconify icon={'healthicons:ui-user-profile'} />
              </IconButton>
              <Typography
                sx={{ color: theme.palette.grey[500], fontSize: { md: '13px', xs: '11px' } }}
                onClick={handleClick}
              >
                {Strings.taskBoard.members}
              </Typography>
              {selectedMembers.length > 0 && (
                <IconButton
                  aria-label="clear filters"
                  sx={{
                    color: theme.palette.primary.dark,
                    p: 0,
                  }}
                  onClick={clearFilters}
                >
                  <Iconify icon={'tdesign:filter-clear'} />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
        <Stack ml={2} gap={1}>
          {taskAssignment?.length > 0 && (
            <Stack sx={{ flexDirection: 'row' }} gap={1}>
              <CustomAvatarGroup size="tiny" max={10} spacing={10}>
                {selectedMembers.length > 0
                  ? selectedMembers.map((member, index) => (
                      <CustomAvatar key={index} name={member} color="info" />
                    ))
                  : getUniqueManagers(taskAssignment).map((manager: any, index: number) => (
                      <CustomAvatar key={index} name={manager.assignedTo.firstName} color="info" />
                    ))}
              </CustomAvatarGroup>
            </Stack>
          )}
        </Stack>
        <Scrollbar sx={{ height: { md: 250 }, maxHeight: { xs: 250 } }}>
          {filteredTasks?.length > 0 ? (
            filteredTasks.map((option: IProject) => (
              <InfoStatusCard
                key={option.id}
                titleName={option.cardName}
                subTitleHead={`${Strings.dashboard.assignedBy}:`}
                subTitleValue={`${option.assignedBy.firstName} ${option.assignedBy.lastName}`}
                status={option.priority}
                actionTime={'Due Date:'}
                actionSubValue={dayjs(option.endTime).format('DD/MM/YYYY')}
                image={option.projectImage}
              />
            ))
          ) : (
            <DataNotFound height={250} />
          )}
        </Scrollbar>
      </Card>
      <MemberListPopover
        open={open}
        anchorEl={anchorEl}
        handleClose={handleClose}
        selectedMembers={selectedMembers}
        addSelectedMember={addSelectedMember}
        searchProps={searchProps}
      />
      <Card
        sx={{
          width: { sm: '97%', md: '50%' },
          height: { md: '300px' },
          maxHeight: { xs: '300px' },
          margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
          marginTop: { xs: '2%', sm: '2%', md: '0%' },
          borderRadius: '5px',
        }}
      >
        <StyledTitle>{Strings.employeeDashborad.assets}</StyledTitle>
        <Scrollbar sx={{ height: { md: 250 }, maxHeight: { xs: 250 } }}>
          {data?.assets && data?.assets.length > 0 ? (
            data.assets.map((option: IAssets) => {
              const openDetailsPage = () => {
                navigate(PATH_DASHBOARD.assetsDetail, { state: { id: option.id } });
              };
              return (
                <InfoStatusCard
                  key={option.id}
                  titleName={option.assetName}
                  subTitleHead={`${Strings.dashboard.assignedOn}:`}
                  subTitleValue={dayjs(option.createdAt).format('DD/MM/YYYY')}
                  status={option.status}
                  image={option.image}
                  onClick={openDetailsPage}
                />
              );
            })
          ) : (
            <DataNotFound height={230} />
          )}
        </Scrollbar>
      </Card>
    </Stack>
  );
};

export { MangerAssetsAndTasksPage };
